import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/status/status-badge";
import { api, ApiError } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import { getCachedEvents, setCachedEvents } from "@/lib/events-cache";

type UpcomingEvent = {
  provider:
    | "zoom"
    | "google_meet"
    | "google_forms"
    | "linkedin_events"
    | "heysummit"
    | "fluent_forms"
    | "constant_contact"
    | "eventbrite"
    | "microsoft_teams";
  connectionId: string;
  id: string;
  resourceType: string;
  name: string;
  startTime?: string | null;
  registrantCount?: number | null;
  alreadyConfigured: boolean;
};

function providerLabel(provider: UpcomingEvent["provider"]): string {
  switch (provider) {
    case "google_meet":
      return "Google Meet";
    case "google_forms":
      return "Google Forms";
    case "linkedin_events":
      return "LinkedIn Events";
    case "heysummit":
      return "HeySummit";
    case "fluent_forms":
      return "Fluent Forms";
    case "constant_contact":
      return "Constant Contact";
    case "eventbrite":
      return "Eventbrite";
    case "microsoft_teams":
      return "Microsoft Teams";
    default:
      return "Zoom";
  }
}

export function UpcomingEventsPage() {
  const { orgId, canManage } = useOrg();
  const navigate = useNavigate();
  const [items, setItems] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  async function load(cursor?: string | null, append = false) {
    if (append) setLoadingMore(true);
    else if (items.length === 0) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (cursor) params.set("cursor", cursor);
      params.set("limit", "100");
      const res = await api<{
        items: UpcomingEvent[];
        nextCursor: string | null;
        hash?: string;
      }>(`/api/accounts/${orgId}/upcoming-events?${params.toString()}`);
      if (append) {
        setItems((prev) => [...prev, ...(res.items ?? [])]);
      } else {
        setItems(res.items ?? []);
        if (res.hash) setCachedEvents(orgId, res.items ?? [], res.hash);
      }
      setNextCursor(res.nextCursor ?? null);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not load upcoming events",
      );
      if (!append && items.length === 0) setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    // Stale-while-revalidate: show cached events instantly, then check for updates
    const cached = getCachedEvents(orgId);
    if (cached) {
      setItems(cached.events as UpcomingEvent[]);
      setLoading(false);
    }

    void (async () => {
      try {
        const check = await api<{ hash: string | null; count: number }>(
          `/api/accounts/${orgId}/upcoming-events/check`,
        );
        if (check.hash && check.hash === cached?.hash) {
          return; // No change — keep showing cached data
        }
      } catch {
        // Check failed — proceed with full fetch
      }
      await load();
    })();
  }, [orgId]);

  async function createStream(ev: UpcomingEvent) {
    if (!canManage || creatingId) return;
    setCreatingId(`${ev.provider}:${ev.id}`);
    setError(null);
    try {
      const res = await api<{ conferenceId: string }>(
        `/api/accounts/${orgId}/quick-streams`,
        {
          method: "POST",
          body: JSON.stringify({
            provider: ev.provider,
            connectionId: ev.connectionId,
            resourceType: ev.resourceType,
            resourceId: ev.id,
            name: ev.name,
          }),
        },
      );
      navigate(`/conferences/${res.conferenceId}`);
    } catch (e) {
      if (e instanceof ApiError && e.code === "DESTINATION_REQUIRED") {
        setError(
          "Connect a CRM destination on Integrations first, then create a stream.",
        );
      } else {
        setError(
          e instanceof Error ? e.message : "Could not create sync stream",
        );
      }
    } finally {
      setCreatingId(null);
    }
  }

  if (loading && items.length === 0) return <PageSkeleton />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-rs-ink">Upcoming Events</h1>
          <p className="mt-1 text-sm text-rs-ink-muted">
            Events from all of your connected sources, ordered by date and time.
            Create a sync stream in one click. Streams are named after the event
            and use your existing CRM destination with default field mappings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {refreshing ? (
            <span className="text-xs text-rs-ink-muted animate-pulse">
              Checking for updates…
            </span>
          ) : null}
          <Button variant="secondary" onClick={() => void load()}>
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-rs-danger" role="alert">
          {error}{" "}
          {error.includes("destination") ? (
            <Link to="/integrations" className="underline underline-offset-2">
              Open Integrations
            </Link>
          ) : null}
        </p>
      ) : null}

      <Panel>
        <PanelHeader title="Meetings Ready To Sync" />
        {items.length === 0 ? (
          <div className="p-5">
            <EmptyState
              title="No Upcoming Events"
              description="Connect a source on Integrations, then refresh to see events you can sync."
              actionLabel="Open Integrations"
              onAction={() => navigate("/integrations")}
            />
          </div>
        ) : (
          <ul className="divide-y divide-rs-border">
            {items.map((ev) => {
              const key = `${ev.provider}:${ev.id}`;
              const busy = creatingId === key;
              return (
                <li
                  key={key}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-rs-ink">
                      {ev.name}
                    </p>
                    <p className="text-xs text-rs-ink-muted">
                      {providerLabel(ev.provider)}
                      {ev.startTime
                        ? ` · ${new Date(ev.startTime).toLocaleString()}`
                        : ""}
                      {ev.registrantCount != null
                        ? ` · ${ev.registrantCount} registrants`
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {ev.alreadyConfigured ? (
                      <StatusBadge tone="ready">Already Synced</StatusBadge>
                    ) : (
                      <Button
                        size="sm"
                        disabled={!canManage || busy || Boolean(creatingId)}
                        onClick={() => void createStream(ev)}
                      >
                        {busy ? "Creating…" : "Create Stream"}
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {nextCursor && (
          <div className="px-5 py-3 text-center border-t divide-rs-border">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void load(nextCursor, true)}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading…" : "Load More"}
            </Button>
          </div>
        )}
      </Panel>
    </div>
  );
}
